
import { eoRendererMixin, metaDataMixin, jsonldMusicMixin } from '~/mixins'

export default {
  mixins: [
    eoRendererMixin,
    metaDataMixin,
    jsonldMusicMixin,
  ],
  layout: 'music',
  async asyncData({ $api, error, params: { slug } }) {
    try{
      let {
        data: {
          items: [ playlist = {} ],
          items: [ { songs: songs = [] } = {} ],
        } = {},
      } = await $api.songselections.getOneBySlug(slug)

      if (!playlist.id) {
        error({
          statusCode: 404,
          message: '(404) Afspeellijst niet gevonden',
          context: `Couldn't find playlist: ${slug}.`,
          severity: 'info',
        })
        return false
      }

      return {
        ...playlist,
        currentSong: songs[0],
        songs,
        seo: {
          description: playlist.description,
          image: playlist.hero,
          title: `${playlist.title} afspeellijst`,
        },
        social: {
          description: playlist.description,
          image: playlist.hero,
          title: `${playlist.title} afspeellijst`,
        },
      }
    } catch(e) {
      error({
        statusCode: 404,
        message: '(404) Lied niet gevonden',
        context: `Couldn't find song: ${slug}.`,
        severity: 'info',
      })
      return false
    }
  },
  data () {
    return {
      // Song data
      publishOn: null,
      description: null,
      image: {},
      id: null,
      shortTitle: null,
      songs: [],
      title: null,
      // Playlist functionality
      currentSong: {},
      currentSongIndex: null,
      currentSongIsLast: false,
      isLoading: false,
      isPlaying: false,
      likes: 0,
      timeToNextSong: -1,
      // Playlists
      bundleSongs: null,
      popularPlaylists: null,
      recommendedPlaylists: null,
      latestPlaylists: null,
      head: {
        meta: [
          { hid: 'category', content: 'Luisteren' },
          { hid: 'genre', content: 'Muziek' },
          { hid: 'topic', content: 'Zingen & Bidden' },
          { hid: 'type', content: 'Afspeellijst' },
        ],
      },
    }
  },
  async fetch() {
    try {
      const [
        { data: { items: popularPlaylists = [] } = {} },
        { data: { items: recommendedPlaylists = [] } = {} },
        { data: { items: latestPlaylists = [] } = {} },
        { data: { item: { likes = 0 } = {} } = {} },
      ] = await Promise.all([
        this.$api.songselections.getAll({
          domain: this.$config.preprEnvironmentDomain,
          channel: 'most-listened-playlists',
          limit: 12,
        }),
        this.$api.songselections.getAll({
          domain: this.$config.preprEnvironmentDomain,
          channel: 'recommended-playlists',
          limit: 12,
        }),
        this.$api.songselections.getAll({
          domain: this.$config.preprEnvironmentDomain,
          limit: 12,
        }),
        this.$api.songselections.getAllNested(
          "likes",
          this.id,
        ),
      ])
      this.popularPlaylists = popularPlaylists
      this.recommendedPlaylists = recommendedPlaylists
      this.latestPlaylists = latestPlaylists
      this.likes = likes
    } catch (e) {
      this.$log.warn(e)
    }
  },
  computed: {
    currentBundle () {
     return this.songs.find(song => song.id === this.currentSong.id)?.metadata?.bundle
    },
    randomOtherPlaylist () {
      return this.latestPlaylists[Math.ceil(Math.random() * this.latestPlaylists.length)]
    },
    isNew () {
      return this.dateFormat.daysInPast(this.publishOn) <= 10
    },
  },
  watch: {
    currentBundle: {
      handler: async function (bundle) {
        // Update songs from bundle whenever a new song is selected
        if (!bundle || bundle.length === 0) { 
          this.bundleSongs = []
        }
        try {
          const { data: { items: newBundleSongs = [] } = {} } = await this.$api.songs.getAll({
            bundle: bundle,
            limit: 48,
          })
          this.bundleSongs = newBundleSongs
        } catch (e) {
          this.bundleSongs = []
        }
      },
      immediate: true,
    },
  },
  methods: {
    onTimeChanged (time, duration) {
      this.timeToNextSong = duration - time <= 10 ? Math.ceil(duration - time) : -1
    },
    playNextSong() {
      if (this.isLoading || this.currentSongIsLast) {
        return
      }
      this.isLoading = true
      if (this.currentSongIndex > -1 && this.currentSongIndex < this.songs.length - 1) {
        this.selectSong(this.songs[this.currentSongIndex+1].id)
        this.isPlaying = true
      }
    },
    selectSong(id) {
      if (!id) { return }
      this.currentSong = this.songs.find(song => song.id === id)
      this.currentSongIndex = this.songs.findIndex(song => song.id === this.currentSong.id)
      this.currentSongIsLast = this.currentSongIndex == this.songs.length - 1
    },
  },
  jsonld() {
    return {
      '@context': 'http://schema.org',
      '@type': 'MusicPlaylist',
      numTracks: this.songs?.length,
      track: this.songs.map(song => this.renderJsonldMusicRecording(song)),
      audio: this.$config.baseUrlCanonical + this.$route.path,
      dateCreated: this.publishOn,
      description: this.description,
      headline: this.title,
      image: this.image.url,
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': this.$config.baseUrlCanonical + this.$route.path,
      },
      name: this.title,
      publisher: {
        '@type': 'Organization',
        name: 'EO',
        logo: {
          '@type': 'ImageObject',
          url: this.$config.baseUrlCanonical + require('~/assets/logo/evangelische-omroep.webp'),
        },
      },
      url: this.$config.baseUrlCanonical + this.$route.path,
    }
  },
}
